import React from "react";
import { Search, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { DEFAULT_FILTERS, SORTS } from "./orderHelpers";

function OptionGroup({ label, value, options, onChange }) {
  return (
    <fieldset className="space-y-1.5">
      <legend className="text-xs font-semibold text-muted-foreground">{label}</legend>
      <div className="flex flex-wrap gap-1.5">
        {options.map(([key, text]) => (
          <button
            key={key}
            type="button"
            aria-pressed={value === key}
            onClick={() => onChange(key)}
            className={cn(
              "rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
              value === key
                ? "border-primary bg-primary text-primary-foreground shadow-sm"
                : "border-border bg-card text-muted-foreground hover:bg-surface hover:text-primary",
            )}
          >
            {text}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Filtros avançados da Gestão de Pedidos: busca, pagamento, entrega,
 * período e ordenação. Aplicados na hora (sem botão "aplicar").
 */
const OrderFiltersSheet = ({ open, onOpenChange, filters, onChange, resultCount }) => {
  const set = (patch) => onChange({ ...filters, ...patch });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-sm">
        <SheetHeader className="border-b px-4 py-4 text-left">
          <SheetTitle className="font-display text-primary">Filtros</SheetTitle>
          <SheetDescription>Refine a lista de pedidos.</SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
          <div className="space-y-1.5">
            <Label htmlFor="orders-search" className="text-xs font-semibold text-muted-foreground">
              Buscar
            </Label>
            <div className="relative flex items-center">
              <Search className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="orders-search"
                type="search"
                value={filters.search}
                onChange={(e) => set({ search: e.target.value })}
                placeholder="Código do pedido ou cliente"
                className="h-10 pl-9"
              />
            </div>
          </div>

          <OptionGroup
            label="Pagamento"
            value={filters.payment}
            onChange={(payment) => set({ payment })}
            options={[
              ["all", "Todos"],
              ["paid", "Pagos"],
              ["unpaid", "A receber"],
            ]}
          />

          <OptionGroup
            label="Tipo de entrega"
            value={filters.delivery}
            onChange={(delivery) => set({ delivery })}
            options={[
              ["all", "Todos"],
              ["delivery", "Delivery"],
              ["pickup", "Retirada"],
            ]}
          />

          <div className="space-y-2">
            <OptionGroup
              label="Período"
              value={filters.period}
              onChange={(period) => set({ period })}
              options={[
                ["all", "Todo o período"],
                ["today", "Hoje"],
                ["custom", "Personalizado"],
              ]}
            />
            {filters.period === "custom" && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="orders-start" className="text-[11px] text-muted-foreground">
                    De
                  </Label>
                  <Input
                    id="orders-start"
                    type="date"
                    value={filters.startDate}
                    max={filters.endDate || undefined}
                    onChange={(e) => set({ startDate: e.target.value })}
                    className="h-10"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="orders-end" className="text-[11px] text-muted-foreground">
                    Até
                  </Label>
                  <Input
                    id="orders-end"
                    type="date"
                    value={filters.endDate}
                    min={filters.startDate || undefined}
                    onChange={(e) => set({ endDate: e.target.value })}
                    className="h-10"
                  />
                </div>
              </div>
            )}
          </div>

          <OptionGroup
            label="Ordenar por"
            value={filters.sortBy}
            onChange={(sortBy) => set({ sortBy })}
            options={Object.entries(SORTS)}
          />
        </div>

        <SheetFooter className="flex-row gap-2 border-t px-4 py-3 pb-safe sm:space-x-0">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => onChange(DEFAULT_FILTERS)}
          >
            <X className="mr-1.5 h-4 w-4" />
            Limpar
          </Button>
          <Button
            type="button"
            className="flex-1 hover:bg-brand-dark"
            onClick={() => onOpenChange(false)}
          >
            Ver {resultCount} {resultCount === 1 ? "pedido" : "pedidos"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default OrderFiltersSheet;
